if (!customElements.get('contact-file-upload')) {
  class ContactFileUpload extends HTMLElement {
    connectedCallback() {
      this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 1;
      this.trigger = this.querySelector('[data-contact-file-upload-trigger]');
      this.statusText = this.querySelector('[data-contact-file-upload-status]');
      this.liveRegion = this.querySelector('[data-contact-file-upload-live]');
      this.carrierInput = this.querySelector('.appFileUpload');
      this.nativeInput = null;
      this.uploading = false;
      this.pendingCount = 0;
      this.pendingFilename = '';
      this._rejected = false;
      this._savedAlert = null;
      this._patchedAlert = null;

      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.trigger?.addEventListener('click', () => this._handleTriggerClick(), { signal: this.signal });
      this.addEventListener('dragenter', (event) => this._handleDragEnter(event), { signal: this.signal });
      this.addEventListener('dragover', (event) => this._handleDragOver(event), { signal: this.signal });
      this.addEventListener('dragleave', (event) => this._handleDragLeave(event), { signal: this.signal });
      this.addEventListener('dragend', () => this.classList.remove('is-dragover'), { signal: this.signal });
      this.addEventListener('drop', (event) => this._handleDrop(event), { signal: this.signal });

      this._installCarrierValueHook();

      this._nativeObserver = new MutationObserver(() => this._findNativeInput());
      this._nativeObserver.observe(this, { childList: true, subtree: true });
      this._findNativeInput();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this._nativeObserver?.disconnect();
      if (this.carrierInput) delete this.carrierInput.value;
    }

    _installCarrierValueHook() {
      if (!this.carrierInput) return;

      const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      Object.defineProperty(this.carrierInput, 'value', {
        configurable: true,
        get: descriptor.get,
        set: (value) => {
          descriptor.set.call(this.carrierInput, value);
          this._handleCarrierValue(value);
        },
      });
    }

    _findNativeInput() {
      const nativeInput = this.querySelector('.appFileUploadNative');
      if (!nativeInput) return;

      this.nativeInput = nativeInput;
      this.nativeInput.multiple = this.maxFiles > 1;
      this.nativeInput.classList.add('visually-hidden');
      this.nativeInput.tabIndex = -1;
      this.nativeInput.addEventListener('change', () => this._handleNativeChangeCapture(), { capture: true, signal: this.signal });
      this.nativeInput.addEventListener('change', () => this._handleNativeChange(), { signal: this.signal });
      this._nativeObserver?.disconnect();
    }

    _handleTriggerClick() {
      if (!this.nativeInput) {
        this._setText(this.dataset.textError, true);
        return;
      }
      this.nativeInput.click();
    }

    _handleDragEnter(event) {
      event.preventDefault();
      this.classList.add('is-dragover');
    }

    _handleDragOver(event) {
      event.preventDefault();
    }

    _handleDragLeave(event) {
      if (this.contains(event.relatedTarget)) return;
      this.classList.remove('is-dragover');
    }

    _handleDrop(event) {
      event.preventDefault();
      this.classList.remove('is-dragover');

      if (!this.nativeInput) {
        this._setText(this.dataset.textError, true);
        return;
      }

      const files = Array.from(event.dataTransfer?.files || []).slice(0, this.maxFiles);
      if (!files.length) return;

      const dataTransfer = new DataTransfer();
      files.forEach((file) => dataTransfer.items.add(file));
      this.nativeInput.files = dataTransfer.files;
      this.nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _handleNativeChangeCapture() {
      const savedAlert = window.alert;
      const patchedAlert = () => {
        this._rejected = true;
        this.uploading = false;

        const text = this.nativeInput.files.length > this.maxFiles
          ? this.dataset.textErrorCount.replace('__COUNT__', this.maxFiles)
          : this.dataset.textErrorSize;
        this._setText(text, true);
      };

      this._savedAlert = savedAlert;
      this._patchedAlert = patchedAlert;
      window.alert = patchedAlert;

      setTimeout(() => this._restoreAlert(), 0);
    }

    _restoreAlert() {
      if (window.alert === this._patchedAlert) {
        window.alert = this._savedAlert;
      }
      this._patchedAlert = null;
      this._savedAlert = null;
    }

    _handleNativeChange() {
      this._restoreAlert();

      const files = this.nativeInput?.files;
      const count = files?.length || 0;

      if (!count) {
        this.uploading = false;
        if (this._rejected) {
          this._rejected = false;
          return;
        }
        this._setText(this.dataset.textIdle);
        return;
      }

      this.uploading = true;
      this.pendingCount = count;
      this.pendingFilename = files[0]?.name || '';

      const text = count > 1
        ? this.dataset.textUploadingMultiple.replace('__COUNT__', count)
        : this.dataset.textUploading;
      this._setText(text);
    }

    _handleCarrierValue(value) {
      if (!this.uploading) return;

      this.uploading = false;

      if (!value) {
        this._setText(this.dataset.textError, true);
        return;
      }

      const text = this.pendingCount > 1
        ? this.dataset.textUploadedMultiple.replace('__COUNT__', this.pendingCount)
        : this.dataset.textUploaded.replace('__FILENAME__', this.pendingFilename);
      this._setText(text);
    }

    _setText(text, isError = false) {
      this.classList.toggle('is-error', isError);
      if (this.statusText) this.statusText.textContent = text;
      if (this.liveRegion) this.liveRegion.textContent = text;
    }
  }

  customElements.define('contact-file-upload', ContactFileUpload);
}
